import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp, Flag, Lightbulb, PartyPopper } from 'lucide-react';
import { guideSteps, isTaskDone, type GuideBaseline, type GuideSnapshot } from './guideSteps';

interface TourPanelProps {
  stepIndex: number;
  snapshot: GuideSnapshot | null;
  baseline: GuideBaseline;
  allDone: boolean;
  finishLabel: string;
  onNext: () => void;
  onPrev: () => void;
  onExit: () => void;
}

export default function TourPanel({ stepIndex, snapshot, baseline, allDone, finishLabel, onNext, onPrev, onExit }: TourPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const step = guideSteps[stepIndex];
  const isLast = stepIndex === guideSteps.length - 1;
  const done = (taskIndex: number) => snapshot !== null && isTaskDone(step.tasks[taskIndex], snapshot, baseline);
  const doneCount = step.tasks.filter((_, i) => done(i)).length;
  const stepDone = doneCount === step.tasks.length;

  return (
    <div className="fixed z-[45] bottom-3 inset-x-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px] animate-slide-up" role="dialog" aria-label="Guidad genomgång">
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl ring-1 ring-white/10 overflow-hidden">
        <div className="flex items-center gap-2 px-5 pt-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-300">
            Steg {stepIndex + 1} av {guideSteps.length}
          </span>
          <div className="flex-1" />
          <button
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Visa guiden' : 'Minimera guiden'}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            {collapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        <div className="flex gap-1 px-5 mt-2.5">
          {guideSteps.map((s, i) => (
            <div key={s.id} className="h-1 flex-1 rounded-full bg-white/10 overflow-hidden">
              <div className={`h-full rounded-full bg-amber-400 transition-all duration-500 ${i < stepIndex ? 'w-full' : i === stepIndex ? 'w-1/2' : 'w-0'}`} />
            </div>
          ))}
        </div>

        <div className="px-5 pt-3.5 pb-1">
          <h2 className="text-base font-semibold leading-tight">{step.title}</h2>
        </div>

        {!collapsed && (
          <div className="px-5 space-y-4 pb-1">
            <p className="text-sm text-slate-300 leading-relaxed">{step.description}</p>

            <div>
              <p className="text-xs font-medium text-slate-400 mb-2">Prova själv ({doneCount}/{step.tasks.length})</p>
              <ul className="space-y-1.5">
                {step.tasks.map((task, i) => (
                  <li key={task.id} className="flex items-start gap-2.5 text-sm">
                    <span className={`mt-0.5 flex-shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all duration-300 ${done(i) ? 'bg-emerald-500' : 'border-2 border-slate-600'}`}>
                      {done(i) && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </span>
                    <span className={`transition-colors ${done(i) ? 'text-slate-400 line-through' : 'text-slate-100'}`}>{task.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {allDone ? (
              <div className="flex gap-2.5 bg-emerald-500/15 rounded-xl px-3 py-2.5">
                <PartyPopper className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-emerald-100 leading-relaxed">Allt klart! Föreningen är redo att användas fullt ut.</p>
              </div>
            ) : (
              <div className="flex gap-2.5 bg-white/5 rounded-xl px-3 py-2.5">
                <Lightbulb className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-300 leading-relaxed">{step.tip}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 px-5 py-4">
          <button onClick={onExit} className="text-xs text-slate-400 hover:text-white transition-colors mr-auto">
            Avsluta guiden
          </button>
          {stepIndex > 0 && (
            <button
              onClick={onPrev}
              aria-label="Föregående steg"
              className="p-2 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onNext}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${stepDone ? 'bg-amber-400 text-slate-900 hover:bg-amber-300' : 'bg-white/10 text-white hover:bg-white/20'}`}
          >
            {isLast ? <>{finishLabel} <Flag className="w-4 h-4" /></> : <>Nästa <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
